// MessageTemplateCard.js — Single card in the flat message-template list.
//
// Shows template name, body preview (line-clamp-2), a soft character-count hint
// (D-07: no color change, no segment count), and a per-card ⋯ overflow menu.
// The overflow menu actions are Edit, Duplicate, and Delete.
//
// All colors use CSS custom properties — no literal hex (UI-SPEC Color contract).

import React from 'react';

// ─── Character count hint ──────────────────────────────────────────────────────

function charCountHint(body = '') {
    const n = (body || '').length;
    if (n <= 160) return `${n} characters`;
    return `${n} characters — tokens expand at send time`;
}

// ─── Icons ──────────────────────────────────────────────────────────────────────

function DotsHorizontalIcon({ className = 'w-5 h-5' }) {
    return (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <circle cx="4" cy="10" r="1.5" />
            <circle cx="10" cy="10" r="1.5" />
            <circle cx="16" cy="10" r="1.5" />
        </svg>
    );
}

// ─── Per-card overflow menu ────────────────────────────────────────────────────

function CardOverflowMenu({ onEdit, onDuplicate, onDelete }) {
    const [open, setOpen] = React.useState(false);
    const menuRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const item = (label, onClick, danger = false) => (
        <button
            key={label}
            type="button"
            onClick={() => { setOpen(false); onClick(); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[color:var(--surface-hover)] transition-colors"
            style={{ color: danger ? 'var(--color-danger)' : 'var(--text-primary)' }}
        >
            {label}
        </button>
    );

    return (
        <div ref={menuRef} className="relative" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                aria-label="More options"
                onClick={() => setOpen((v) => !v)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-[color:var(--text-muted)] hover:bg-[color:var(--surface-hover)] transition-colors"
            >
                <DotsHorizontalIcon />
            </button>
            {open && (
                <div
                    role="menu"
                    className="absolute right-0 top-10 z-50 w-44 rounded-lg border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-lg py-1"
                >
                    {item('Edit', onEdit)}
                    {item('Duplicate', onDuplicate)}
                    {item('Delete', onDelete, true)}
                </div>
            )}
        </div>
    );
}

// ─── MessageTemplateCard ───────────────────────────────────────────────────────

export default function MessageTemplateCard({ template, onEdit, onDuplicate, onDelete }) {
    return (
        <div
            className="rounded-xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] p-4 hover:border-[color:var(--color-accent)]/40 hover:shadow-sm transition-all cursor-pointer"
            onClick={() => onEdit(template)}
        >
            {/* Name */}
            <p
                className="text-sm font-semibold leading-[1.4] truncate"
                style={{ color: 'var(--text-primary)' }}
            >
                {template.name || 'Untitled template'}
            </p>

            {/* Body preview */}
            <p
                className="text-xs mt-1 line-clamp-2 leading-[1.5]"
                style={{ color: 'var(--text-muted)' }}
            >
                {template.body || <span style={{ fontStyle: 'italic' }}>No content yet.</span>}
            </p>

            {/* Footer: char count hint + overflow menu */}
            <div className="flex items-center justify-between mt-3">
                <span
                    className="text-xs"
                    style={{ color: 'var(--text-muted)' }}
                >
                    {charCountHint(template.body)}
                </span>
                <CardOverflowMenu
                    onEdit={() => onEdit(template)}
                    onDuplicate={() => onDuplicate(template)}
                    onDelete={() => onDelete(template)}
                />
            </div>
        </div>
    );
}
